import {zodResolver} from "@hookform/resolvers/zod";
import {useAddressVerification} from "@mytour/platform";
import {
  type AddressComparisonFields,
  type DepartureAddressView,
  hasAddressChanged,
} from "@mytour/shared";
import {
  AddressVerificationSection,
  type ManualPinSeed,
  type StructuredReplacementFields,
} from "@mytour/ui/form";
import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {Alert, Button, Form, Spinner} from "reactstrap";
import {z} from "zod";
import {
  useDepartureAddress,
  useUpdateDepartureAddress,
} from "../../query/departureAddressQueries.js";

const departureAddressSchema = z.object({
  streetAddress: z.string().trim().min(1, "L'adresse est requise"),
  streetAddress2: z.string().optional(),
  zipCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/, "Le code postal doit contenir 5 chiffres"),
  city: z.string().trim().min(1, "La ville est requise"),
  /**
   * Trio pin manuel (#941, même doctrine présence-based que le payload —
   * voir `customerFormSchema.ts`) : jamais posé par un `Controller`, écrit
   * uniquement par `setValue` depuis `payloadPin` (`useAddressVerification`).
   * Pas de `.default()` : `undefined` = "jamais touché", distinct de `null` = "retiré".
   */
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  geocodeSource: z.literal("manual").nullable().optional(),
});

type DepartureAddressFormData = z.infer<typeof departureAddressSchema>;

type Props = {
  /**
   * `false` pendant que la modale d'onboarding (#1124) est ouverte sur la
   * même adresse : suspend la vérification IGN de CE formulaire pour éviter
   * le double appel IGN et la double télémétrie `geocode_confirmation_shown`
   * qu'un second `useAddressVerification` monté en parallèle produirait.
   */
  enabled?: boolean;
};

export function DepartureAddressForm({enabled = true}: Props = {}) {
  const {departureAddress, isDepartureAddressLoading} = useDepartureAddress();
  const {updateDepartureAddress, isUpdatingDepartureAddress} = useUpdateDepartureAddress();

  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * Adresse au chargement (#864) : ne bloque la soumission sur un géocodage en
   * vol QUE si l'utilisateur a réellement retouché l'adresse depuis le chargement
   * — posée par le même effet que `reset()` ci-dessous, sur les mêmes données.
   */
  const initialAddressRef = useRef<AddressComparisonFields | null>(null);

  const {control, handleSubmit, watch, setValue, reset, trigger, formState} =
    useForm<DepartureAddressFormData>({
      resolver: zodResolver(departureAddressSchema),
      defaultValues: {
        streetAddress: "",
        streetAddress2: "",
        zipCode: "",
        city: "",
      },
      /**
       * Aligné sur CustomerForm (#995) : requis pour que `formState.isValid`
       * reflète la saisie en direct plutôt qu'uniquement après une tentative
       * de soumission — condition du gate ci-dessous.
       */
      mode: "onChange",
    });

  const streetAddress = watch("streetAddress");
  const streetAddress2 = watch("streetAddress2");
  const zipCode = watch("zipCode");
  const city = watch("city");

  /**
   * Machine à états de vérification d'adresse (#941/#937/#980) — source
   * unique lue par le badge, le bouton de soumission (`isSaveDisabled`) et
   * `handleSubmitGate`. La section adresse reste toujours visible, aucun
   * toggle ne la conditionne (#1113) — seule la vérification IGN elle-même
   * peut être suspendue via `enabled` (#1124, voir la prop du composant).
   */
  const verification = useAddressVerification({
    fields: {line1: streetAddress, line2: streetAddress2 ?? null, zip: zipCode, city},
    enabled,
  });
  // Trio pin manuel écrit dans le payload (#941) — `payloadPin` reste
  // `undefined` tant que rien n'a changé depuis le chargement (présence-based).
  useEffect(() => {
    if (verification.payloadPin === undefined) {
      return;
    }
    setValue("latitude", verification.payloadPin?.lat ?? null);
    setValue("longitude", verification.payloadPin?.lng ?? null);
    setValue("geocodeSource", verification.payloadPin ? "manual" : null);
  }, [verification.payloadPin, setValue]);

  /**
   * Empreinte de la dernière hydratation : le cache query produit une NOUVELLE
   * référence après chaque sauvegarde (`setQueryData`) même à données
   * identiques — sans cette comparaison par valeur, l'effet ci-dessous
   * rejouerait `verification.reset()` à chaque save, donc un re-géocodage IGN
   * complet et un badge qui clignote pour rien.
   */
  const hydratedKeyRef = useRef<string | null>(null);
  /**
   * Hydrate le formulaire depuis l'adresse de départ chargée — toujours
   * celle du membre, jamais celle du siège (#1113).
   */
  useEffect(() => {
    if (!departureAddress) {
      return;
    }
    const {departureAddress: current} = departureAddress;
    const key = hydrationKeyOf(current);
    if (hydratedKeyRef.current === key) {
      return;
    }
    hydratedKeyRef.current = key;
    reset({
      streetAddress: current.streetAddress,
      streetAddress2: current.streetAddress2 ?? "",
      zipCode: current.zipCode,
      city: current.city,
    });
    /**
     * `formState.isValid` est déjà juste après `reset()` (RHF le recalcule).
     * Ce `trigger()` peuple `errors` immédiatement, pour qu'une fiche
     * legacy/modération invalide affiche ses champs en rouge dès l'ouverture
     * plutôt qu'après une première frappe (même rôle que sur CustomerForm).
     */
    trigger();
    initialAddressRef.current = {
      line1: current.streetAddress,
      line2: current.streetAddress2 ?? null,
      zip: current.zipCode,
      city: current.city,
    };
    verification.reset({manualPin: manualPinSeedOf(current)});
  }, [departureAddress, reset, trigger, verification.reset]);

  if (isDepartureAddressLoading) {
    return <Spinner size="sm" />;
  }

  const disabled = isUpdatingDepartureAddress;
  /**
   * Verdict de résolution (#980) : gate strict, comme sur la fiche client et
   * la modale d'onboarding (#1113). `!formState.isValid` couvre en plus une
   * donnée legacy/modération invalide sur un champ verrouillé par un pin
   * manuel gelé — le bouton actif ne menait qu'à un échec de soumission
   * silencieux (#1132/ex-#1123, même fix que CustomerForm).
   */
  const isSaveDisabled = disabled || !verification.resolved || !formState.isValid;

  return (
    <>
      <Form onSubmit={handleSubmit(handleSubmitGate)}>
        <AddressVerificationSection
          verification={verification}
          onReplace={handleReplaceAddress}
          control={control}
          names={{line1: "streetAddress", line2: "streetAddress2", zip: "zipCode", city: "city"}}
          errors={formState.errors}
          disabled={disabled}
          zipCitySplit={{zip: 33, city: 65}}
        />

        {saveError && (
          <Alert color="danger" fade={false}>
            {saveError}
          </Alert>
        )}

        <Button color="primary" type="submit" disabled={isSaveDisabled}>
          {isUpdatingDepartureAddress ? <Spinner size="sm" /> : null}
          {isUpdatingDepartureAddress ? " " : ""}Sauvegarder
        </Button>
      </Form>
    </>
  );

  /**
   * Gate de soumission — voir `guardSubmit` (`useAddressVerification.ts`)
   * pour les critères. Le rejet backend 400 sur une adresse imprécise (#797)
   * reste inchangé, en aval de ce gate.
   */
  function handleSubmitGate(data: DepartureAddressFormData) {
    const currentFields: AddressComparisonFields = {
      line1: data.streetAddress,
      line2: data.streetAddress2 ?? null,
      zip: data.zipCode,
      city: data.city,
    };
    const editedSinceLoad =
      initialAddressRef.current != null &&
      hasAddressChanged(initialAddressRef.current, currentFields);
    verification.guardSubmit({
      editedSinceLoad,
      onPass: () => onSubmit(data),
    });
  }

  function handleReplaceAddress(fields: StructuredReplacementFields) {
    setValue("streetAddress", fields.line1, {shouldValidate: true});
    setValue("zipCode", fields.zip, {shouldValidate: true});
    setValue("city", fields.city, {shouldValidate: true});
  }

  async function onSubmit(data: DepartureAddressFormData) {
    setSaveError(null);
    try {
      await updateDepartureAddress({
        streetAddress: data.streetAddress,
        streetAddress2: data.streetAddress2 || undefined,
        zipCode: data.zipCode,
        city: data.city,
        // Présence-based (#941, doctrine `customer.service.ts`) : un
        // formulaire jamais touché par le flux manuel n'envoie pas ces clés.
        ...(data.geocodeSource !== undefined
          ? {geocodeSource: data.geocodeSource, latitude: data.latitude, longitude: data.longitude}
          : {}),
      });
      toast.success("Adresse de départ enregistrée");
    } catch (error: any) {
      setSaveError(
        error?.response?.data?.message ??
          "Cette adresse n'a pas pu être enregistrée. Vérifiez la saisie."
      );
    }
  }
}

// Tout ce que l'effet d'hydratation consomme — un changement de n'importe
// lequel de ces champs justifie une vraie ré-hydratation (reset RHF + vérification).
function hydrationKeyOf(current: DepartureAddressView): string {
  return JSON.stringify([
    current.streetAddress,
    current.streetAddress2 ?? "",
    current.zipCode,
    current.city,
    current.geocodeSource,
    current.latitude,
    current.longitude,
  ]);
}

/**
 * Seed du pin manuel stocké, apparié au texte de l'adresse stockée. Un pin
 * n'est réellement affichable/gelable que s'il porte des coordonnées —
 * `geocodeSource === "manual"` seul ne suffit pas (ex. valeur posée depuis la
 * modération sans lat/lng) : dans ce cas `null`, pour ne jamais laisser
 * `isManualFrozen` passer à `true` sur une adresse sans pin réel — sinon le
 * verdict de résolution (#980) verrait `status: "manual-pin"` (toujours
 * résolu) sur une adresse jamais géocodée.
 */
function manualPinSeedOf(view: DepartureAddressView): ManualPinSeed | null {
  if (view.geocodeSource !== "manual" || view.latitude == null || view.longitude == null) {
    return null;
  }
  return {
    position: {lat: view.latitude, lng: view.longitude},
    fields: {
      line1: view.streetAddress,
      line2: view.streetAddress2 ?? null,
      zip: view.zipCode,
      city: view.city,
    },
  };
}
